import { useMemo } from 'react';
import { useLocale } from '@/i18n/LocaleProvider';
import { useTranslation } from '@/i18n/useTranslation';
import type { AppLanguage } from '@/i18n/translations';
import { patchSessionClientMeta } from '@/lib/api/sessions';
import { WIZARD_SESSION_ID_KEY } from '@/context/WizardSessionContext';
import styles from './LanguageChooser.module.css';

type LanguageOption = { value: AppLanguage; label: string };

const SELECT_ID = 'app-language-select';

function readSessionId(): string | null {
  try {
    return sessionStorage.getItem(WIZARD_SESSION_ID_KEY);
  } catch {
    return null;
  }
}

export default function LanguageChooser() {
  const { language, setLanguage } = useLocale();
  const t = useTranslation();

  const options = useMemo<LanguageOption[]>(
    () => [
      { value: 'he', label: t.langSelect.hebrew },
      { value: 'en', label: t.langSelect.english },
      { value: 'ar', label: t.langSelect.arabic },
      { value: 'ru', label: t.langSelect.russian },
    ],
    [t.langSelect.arabic, t.langSelect.english, t.langSelect.hebrew, t.langSelect.russian],
  );

  const handleChange = (next: AppLanguage) => {
    setLanguage(next);
    const sessionId = readSessionId();
    if (sessionId) {
      void patchSessionClientMeta(sessionId, { client_meta: { locale: next } });
    }
  };

  return (
    <div className={styles.root} data-testid="language-chooser">
      <label className={styles.label} htmlFor={SELECT_ID}>
        {t.langSelect.languageLabel}
      </label>
      <select
        id={SELECT_ID}
        className={styles.select}
        value={language}
        onChange={(event) => handleChange(event.target.value as AppLanguage)}
        aria-label={t.langSelect.chooseLanguageAria}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
